"use client"

import React, { useState, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { Warehouse, Loader2, X, Calendar, MapPin, FileText, Check, ArrowRight, Package } from "lucide-react"
import { Button } from "../../ui/button"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"
import { yardApi, type Yard } from "../../../services/yardApi"
import { taskAPI } from "../../../services/taskApi"

interface StoreToYardResult {
  yardId: number
  yardName: string
  yardAddress?: string
  destinationType: 'yard'
  destinationId: number
}

interface StoreToYardModalProps {
  open: boolean
  onClose: () => void
  taskId: number
  taskNumber: string
  containerNumber: string
  onSuccess: (result: StoreToYardResult) => void
}

export function StoreToYardModal({
  open,
  onClose,
  taskId,
  taskNumber,
  containerNumber,
  onSuccess,
}: StoreToYardModalProps) {
  const [loading, setLoading] = useState(false)
  const [loadingYards, setLoadingYards] = useState(false)
  const [yards, setYards] = useState<Yard[]>([])
  const [selectedYardId, setSelectedYardId] = useState<number | null>(null)
  const [reason, setReason] = useState("")
  const [scheduledDeliveryTime, setScheduledDeliveryTime] = useState("")
  const [error, setError] = useState("")

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !loading) {
      onClose()
    }
  }, [loading, onClose])

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, handleKeyDown])

  useEffect(() => {
    if (open) {
      loadYards()
    }
  }, [open])

  const loadYards = async () => {
    setLoadingYards(true)
    setError("")
    try {
      const yardsData = await yardApi.getAllYards({ status: 'active' })
      setYards(yardsData)
      if (yardsData.length === 0) {
        setError("No active yards available")
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load yards")
    } finally {
      setLoadingYards(false)
    }
  }

  const handleSubmit = async () => {
    if (!selectedYardId) {
      setError("Please select a yard")
      return
    }

    setLoading(true)
    setError("")
    try {
      await taskAPI.storeToYard(
        taskId,
        selectedYardId,
        reason.trim() || undefined,
        scheduledDeliveryTime || undefined
      )

      const selectedYard = yards.find(y => y.id === selectedYardId)

      const result = {
        yardId: selectedYardId,
        yardName: selectedYard?.name || 'Yard',
        yardAddress: selectedYard?.address || selectedYard?.location,
        destinationType: 'yard' as const,
        destinationId: selectedYardId
      }
      onSuccess(result)
      handleClose()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string }
      setError(axiosErr.response?.data?.message || axiosErr.message || "Failed to store to yard")
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedYardId(null)
    setReason("")
    setScheduledDeliveryTime("")
    setError("")
    onClose()
  }

  if (!open) return null

  const modalContent = (
    <>
      {/* z-[100]：必须高过详情弹窗的 z-[70]，否则被它的黑底盖住＝点了没反应 */}
      <div
        className="fixed inset-0 z-[100] bg-black/50"
        onClick={handleClose}
      />
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Warehouse className="h-5 w-5 text-violet-600" />
            <h3 className="text-lg font-semibold text-gray-900">Store to Yard</h3>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-5 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Task Info - compact inline */}
          <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-lg">
            <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium text-sm text-gray-900 truncate">{taskNumber}</span>
              <span className="text-gray-300">·</span>
              <span className="text-sm text-gray-500 truncate">{containerNumber}</span>
            </div>
          </div>

          {/* Yard Selection */}
          <div className="space-y-2.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <MapPin className="h-3.5 w-3.5 text-gray-400" />
              Select Yard <span className="text-red-500">*</span>
            </Label>
            {loadingYards ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-violet-400" />
                <span className="ml-2 text-sm text-gray-500">Loading yards...</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                {yards.map((yard) => {
                  const isSelected = selectedYardId === yard.id
                  const availableSlots = yard.available_slots || 0
                  const isFull = availableSlots === 0
                  return (
                    <div
                      key={yard.id}
                      onClick={() => !isFull && setSelectedYardId(yard.id)}
                      className={`relative p-3 border rounded-lg transition-all ${
                        isFull
                          ? "border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                          : isSelected
                            ? "border-violet-500 bg-violet-50/50 ring-1 ring-violet-500/20 cursor-pointer"
                            : "border-gray-200 hover:border-violet-300 hover:bg-violet-50/20 cursor-pointer"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2.5 min-w-0">
                          {/* Radio indicator */}
                          <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected
                              ? "border-violet-500 bg-violet-500"
                              : "border-gray-300"
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">{yard.name}{yard.short_name && yard.short_name !== yard.name ? ` (${yard.short_name})` : ''}</p>
                            <p className="text-xs text-gray-500 truncate">{yard.address || yard.location}</p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            isFull
                              ? "bg-red-50 text-red-600"
                              : availableSlots <= 3
                                ? "bg-amber-50 text-amber-600"
                                : "bg-emerald-50 text-emerald-600"
                          }`}>
                            {isFull ? "Full" : `${availableSlots} / ${yard.total_capacity}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <FileText className="h-3.5 w-3.5 text-gray-400" />
              Reason
              <span className="text-xs font-normal text-gray-400 ml-1">Optional</span>
            </Label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Warehouse appointment not available until tomorrow"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg resize-none h-[72px] text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-colors placeholder:text-gray-400"
            />
          </div>

          {/* Scheduled Delivery Time */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <Calendar className="h-3.5 w-3.5 text-gray-400" />
              Scheduled Delivery
              <span className="text-xs font-normal text-gray-400 ml-1">Optional</span>
            </Label>
            <Input
              type="datetime-local"
              value={scheduledDeliveryTime}
              onChange={(e) => setScheduledDeliveryTime(e.target.value)}
              className="w-full text-sm"
            />
            <p className="text-xs text-gray-400">
              When should the container be delivered from yard to warehouse?
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100">
              <span className="flex-shrink-0 mt-0.5">⚠</span>
              <span>{error}</span>
            </div>
          )}

          {/* Flow Steps - visual stepper */}
          <div className="bg-violet-50/60 rounded-lg p-3.5 border border-violet-100">
            <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-2.5">What happens next</p>
            <div className="flex items-center gap-1 text-xs text-violet-600">
              <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-violet-200 font-medium whitespace-nowrap">
                <MapPin className="h-3 w-3" /> Reroute to Yard
              </span>
              <ArrowRight className="h-3 w-3 text-violet-400 flex-shrink-0" />
              <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-violet-200 font-medium whitespace-nowrap">
                <Package className="h-3 w-3" /> Driver Delivers
              </span>
              <ArrowRight className="h-3 w-3 text-violet-400 flex-shrink-0" />
              <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-violet-200 font-medium whitespace-nowrap">
                <Warehouse className="h-3 w-3" /> Auto Delivery Task
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !selectedYardId}
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Processing...
              </>
            ) : (
              <>
                <Warehouse className="h-4 w-4 mr-1.5" />
                Store to Yard
              </>
            )}
          </Button>
        </div>
      </div>
      </div>
    </>
  )

  return createPortal(modalContent, document.body)
}
